import type { ScrollableElementRef } from '@ps/isp-web-component-library';
import { ScrollableElement } from '@ps/isp-web-component-library';
import { notEmpty } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import { flatMap, sortBy, uniq } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Selection } from '@swf/sports/components';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import type {
  MarketLevelCompoundMarketTemplateProps,
  MarketTemplateComponent
} from '@swf/sports/types/templates';

import useMarketDisplayLimit from '../../hooks/useMarketDisplayLimit';

import { TXTWCL_Show_More } from './PlayerAlternatives.messages.json';
import styles from './PlayerAlternatives.scss';

const SHOW_MORE_THRESHOLD = 10;
const RUNNER_VALUE_PATTERN = /(\d+)\+/;
const STICKY_COLUMN_WIDTH = 136;

const PlayerAlternatives: MarketTemplateComponent<MarketLevelCompoundMarketTemplateProps> = ({
  marketIds,
  config
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const allMarkets = useSelector(widgetSelect.markets);
  const markets = marketIds
    .filter(notEmpty)
    .map((marketId) => allMarkets[marketId])
    .filter(notEmpty);
  const events = useSelector(widgetSelect.events);

  const sortedMarkets = sortBy(
    markets,
    (market) => market.runners[0].winRunnerOdds?.decimalDisplayOdds?.decimalOdds ?? 0
  );

  const [marketRowsToDisplay, handleShowMoreClick, limitRows] = useMarketDisplayLimit(
    sortedMarkets,
    sortedMarkets[0].marketId,
    SHOW_MORE_THRESHOLD
  );

  const runnerValues = flatMap(markets.map((market) => market.runners)).map((runner) =>
    Number(runner.runnerName.match(RUNNER_VALUE_PATTERN)?.[1])
  );

  const sortedRunnerValues = sortBy(uniq(runnerValues), (runnerValue) => Number(runnerValue));

  const ref = useRef<ScrollableElementRef>(null);
  const tdRef = useRef<HTMLTableCellElement>(null);

  const initialScrollStartColumn =
    'initialScrollStartColumn' in config ? config.initialScrollStartColumn : 0;

  useEffect(() => {
    if (initialScrollStartColumn)
      ref.current?.scrollTo(
        (tdRef.current?.offsetLeft ?? STICKY_COLUMN_WIDTH) - STICKY_COLUMN_WIDTH
      );
  }, [initialScrollStartColumn]);

  return (
    <>
      <ScrollableElement ref={ref} buttonVerticalAlignment="top">
        <table className={styles.playerAlternatives}>
          <thead>
            <tr>
              <td className={styles.sticky}>&nbsp;</td>
              {sortedRunnerValues.map((title, idx) => (
                <th
                  scope="col"
                  key={title}
                  ref={idx === initialScrollStartColumn ? tdRef : undefined}
                >
                  {title}+
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {marketRowsToDisplay.map(
              ({
                marketId,
                eventId,
                legTypes,
                marketName,
                marketType,
                marketStatus,
                runners,
                eventTypeId,
                competitionId
              }) => (
                <tr key={marketName}>
                  <th scope="row" className={styles.sticky}>
                    <span>{marketName.split(' - ')[0]}</span>
                  </th>

                  {sortedRunnerValues.map((sortedRunnerValue) => {
                    const runner = runners.find(
                      (r) =>
                        r.runnerName.match(RUNNER_VALUE_PATTERN)?.[1] === String(sortedRunnerValue)
                    );

                    return runner ? (
                      <td key={`${runner.selectionId}`}>
                        <Selection
                          competitionId={competitionId}
                          eventId={eventId}
                          eventTypeId={eventTypeId}
                          handicap={runner.handicap}
                          legTypes={legTypes}
                          marketId={marketId}
                          marketStatus={marketStatus}
                          marketType={marketType}
                          odds={runner.winRunnerOdds}
                          runnerResultType={runner.resultType}
                          runnerStatus={runner.runnerStatus}
                          selectionId={runner.selectionId}
                          startTime={events[eventId].eventStartTime}
                        />
                      </td>
                    ) : (
                      <td key={`cell-${sortedRunnerValue}`}>
                        <span className={styles.emptySelection}>-</span>
                      </td>
                    );
                  })}
                </tr>
              )
            )}
          </tbody>
        </table>
      </ScrollableElement>
      {limitRows && (
        <div className={styles.showMoreWrapper}>
          <button type="button" className={styles.showMore} onClick={handleShowMoreClick}>
            <Translation {...TXTWCL_Show_More} />
          </button>
        </div>
      )}
    </>
  );
};

PlayerAlternatives.marketTemplateType = 'EVENT';

export default PlayerAlternatives;
